import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { PageHeader, PageBody } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Film, Trash2, Download, Play, Layers, X, Wand2, Image as ImageIcon, Zap, User, Send, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import PostToTikTokModal from "@/components/PostToTikTokModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function bytes(n) {
  if (!n) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(2) + " MB";
}

// Phase 3: style chip metadata.
const STYLE_META = {
  faceless:  { label: "Faceless",  icon: Film,       className: "border-zinc-700 bg-zinc-900 text-zinc-300" },
  avatar:    { label: "Avatar",    icon: User,       className: "border-purple-700/60 bg-purple-500/10 text-purple-300" },
  slideshow: { label: "Slideshow", icon: ImageIcon,  className: "border-sky-700/60 bg-sky-500/10 text-sky-300" },
};

export default function LibraryPage() {
  const [videos, setVideos] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const [groupByBatch, setGroupByBatch] = useState(false);
  const [batchFilter, setBatchFilter] = useState(null);  // when set, only show this batch
  const [postingVideo, setPostingVideo] = useState(null);
  const [hasTikTok, setHasTikTok] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/tiktok/accounts`)
      .then((r) => setHasTikTok((r.data?.accounts || []).length > 0))
      .catch(() => setHasTikTok(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/videos`);
      setVideos(r.data);
    } catch (e) {
      toast.error("Failed to load videos");
    }
  }, []);

  // Phase 5.2b — poll active jobs every 5s. Surfaces in-flight + recently-
  // failed renders in real time so jobs no longer "disappear" between
  // load() calls.
  const loadActive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/videos/jobs/active`);
      setActiveJobs(r.data?.items || []);
    } catch (e) {
      // silent — non-critical
    }
  }, []);

  useEffect(() => { load(); loadActive(); }, [load, loadActive]);

  useEffect(() => {
    // Only poll while there are in-flight (non-error) jobs to avoid waste.
    const hasInflight = activeJobs.some((j) => j.stage !== "error" && j.stage !== "needs_review");
    if (pollRef.current) clearInterval(pollRef.current);
    if (hasInflight) {
      pollRef.current = setInterval(() => {
        loadActive();
        load();  // refresh persisted list too in case one just finished
      }, 5000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobs.length, activeJobs.map((j) => j.stage).join(",")]);

  const retryJob = async (job) => {
    // Re-queue a faceless render with the same product context. We use the
    // generic faceless endpoint since failed jobs may have no product_id.
    try {
      const body = {
        product_name: job.product_name || "video",
        product_description: job.error
          ? `Retry of failed job ${job.job_id.slice(0, 8)} (was: ${job.error.slice(0, 80)})`
          : "Retry",
        niche: job.niche || "general",
      };
      await axios.post(`${API}/video/faceless`, body);
      toast.success("Retry queued");
      loadActive();
    } catch (e) {
      toast.error(e.response?.data?.error || "Retry failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video and its file from disk?")) return;
    try {
      await axios.delete(`${API}/videos/${id}`);
      toast.success("Deleted");
      if (previewing === id) setPreviewing(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // Apply batch filter, then optionally group.
  const filteredVideos = useMemo(
    () => batchFilter ? videos.filter((v) => v.batch_id === batchFilter) : videos,
    [videos, batchFilter],
  );

  // Group videos into either a single "all" bucket OR batch-keyed buckets.
  const groups = useMemo(() => {
    if (!groupByBatch) return [{ key: "all", batch_id: null, items: filteredVideos }];
    const byBatch = new Map();
    const noBatch = [];
    for (const v of filteredVideos) {
      if (v.batch_id) {
        if (!byBatch.has(v.batch_id)) byBatch.set(v.batch_id, []);
        byBatch.get(v.batch_id).push(v);
      } else {
        noBatch.push(v);
      }
    }
    const out = [];
    // Batches first (newest first), then ungrouped.
    const batches = [...byBatch.entries()].sort((a, b) => {
      const ta = a[1][0]?.created_at || "";
      const tb = b[1][0]?.created_at || "";
      return tb.localeCompare(ta);
    });
    for (const [bid, items] of batches) {
      out.push({ key: `batch-${bid}`, batch_id: bid, items });
    }
    if (noBatch.length > 0) {
      out.push({ key: "ungrouped", batch_id: null, items: noBatch });
    }
    return out;
  }, [filteredVideos, groupByBatch]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const Card = ({ v }) => {
    const isPreview = previewing === v.id;
    const isFailed = v.status === "failed";
    const fileUrl = `${BACKEND_URL}${v.file_url || ""}`;
    const thumbUrl = v.thumb_url ? `${BACKEND_URL}${v.thumb_url}` : null;
    const style = v.style || "faceless";
    const meta = STYLE_META[style] || STYLE_META.faceless;
    const StyleIcon = meta.icon;
    return (
      <div className={`ap-panel overflow-hidden flex flex-col ${isFailed ? "border border-rose-900/60" : ""}`} data-testid={`video-card-${v.id}`}>
        <div className="aspect-[9/16] bg-zinc-900 relative overflow-hidden">
          {isFailed ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-4 text-center" data-testid={`video-failed-${v.id}`}>
              <AlertTriangle className="h-10 w-10 text-rose-400" />
              <div className="text-xs text-rose-300 mono">Render failed</div>
              <div className="text-[10px] text-zinc-500 break-words line-clamp-6" title={v.error}>{v.error || "unknown error"}</div>
              {v.failed_at_stage && (
                <div className="text-[10px] mono text-zinc-600">at stage: {v.failed_at_stage}</div>
              )}
            </div>
          ) : isPreview ? (
            <video src={fileUrl} controls autoPlay className="w-full h-full object-cover bg-black" data-testid={`video-player-${v.id}`} />
          ) : (
            <button onClick={() => setPreviewing(v.id)} className="w-full h-full relative group" data-testid={`video-play-${v.id}`}>
              {thumbUrl ? (
                <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700"><Film className="h-8 w-8" /></div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                <Play className="h-10 w-10 text-white/90 drop-shadow" />
              </div>
            </button>
          )}
        </div>
        <div className="p-3 flex-1 flex flex-col gap-2">
          {/* Phase 3 style + batch chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className={`text-[10px] mono ${meta.className}`} data-testid={`video-style-${v.id}`}>
              <StyleIcon className="h-2.5 w-2.5 mr-1" /> {meta.label}
            </Badge>
            {isFailed && (
              <Badge variant="outline" className="text-[10px] mono border-rose-700/60 bg-rose-500/10 text-rose-300" data-testid={`video-failed-badge-${v.id}`}>
                Failed
              </Badge>
            )}
            {v.batch_id && (
              <button
                onClick={() => setBatchFilter(v.batch_id)}
                className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-700/60 bg-amber-500/10 text-amber-300 text-[10px] mono hover:bg-amber-500/20 transition-colors"
                title="Filter to this batch"
                data-testid={`video-batch-${v.id}`}
              >
                <Zap className="h-2.5 w-2.5 mr-1" /> Batch
              </button>
            )}
          </div>
          <div className="text-xs text-zinc-400 line-clamp-2" title={v.script?.hook || v.product_name}>
            {v.script?.hook || v.product_name || "(no script)"}
          </div>
          <div className="flex items-center gap-2 text-[10px] mono text-zinc-500 flex-wrap">
            <Badge variant="outline" className="border-zinc-700 mono text-[10px] text-zinc-400">{v.niche}</Badge>
            {v.audio_duration_s != null && <span>{Math.round(v.audio_duration_s || 0)}s</span>}
            {!isFailed && (<><span>·</span><span>{bytes(v.size_bytes)}</span></>)}
            {v.product && <span className="text-emerald-400 truncate">· {v.product.title}</span>}
          </div>
          <div className="flex items-center justify-end gap-1 mt-auto">
            {!isFailed && (
              <a href={fileUrl} download={`${v.id}.mp4`} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-emerald-400" title="Download" data-testid={`video-download-${v.id}`}>
                <Download className="h-3.5 w-3.5" />
              </a>
            )}
            <button onClick={() => remove(v.id)} className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-rose-400" title="Delete" data-testid={`video-delete-${v.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {isFailed ? (
            <button
              onClick={() => retryJob({
                job_id: v.id,
                product_name: v.product_name || v.script?.hook?.slice(0, 60),
                error: v.error, niche: v.niche,
              })}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-rose-700/50 text-rose-300 hover:bg-rose-500/10 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
              data-testid={`video-retry-${v.id}`}>
              <RefreshCw className="h-3 w-3" /> Retry render
            </button>
          ) : hasTikTok ? (
            /* Phase 5.2b — Post to TikTok */
            <button onClick={() => setPostingVideo(v)}
                    className="w-full mt-1 px-3 py-2 rounded-md bg-rose-500 hover:bg-rose-400 text-zinc-950 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
                    data-testid={`post-to-tiktok-btn-${v.id}`}>
              <Send className="h-3 w-3" /> Post to TikTok
            </button>
          ) : (
            <a href="/app/settings"
               className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-500 text-xs inline-flex items-center justify-center gap-1.5"
               data-testid={`post-to-tiktok-btn-disabled-${v.id}`}>
              Connect TikTok in Settings first →
            </a>
          )}
        </div>
      </div>
    );
  };

  // Phase 5.2b — render the in-flight jobs strip at the top of the page.
  const inflightActive = activeJobs.filter((j) => j.stage !== "error" && j.stage !== "needs_review");
  const recentFailures = activeJobs.filter((j) => j.stage === "error");

  const renderActiveJobsStrip = () => {
    if (inflightActive.length === 0 && recentFailures.length === 0) return null;
    return (
      <div className="ap-panel p-3 space-y-2" data-testid="active-jobs-strip">
        {inflightActive.map((j) => (
          <div key={j.job_id} className="flex items-center gap-3" data-testid={`active-job-${j.job_id}`}>
            <Loader2 className="h-3.5 w-3.5 text-emerald-400 animate-spin" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-300 truncate">{j.product_name || j.job_id.slice(0, 8)}</div>
              <div className="text-[10px] mono text-zinc-500">stage: <span className="text-emerald-300">{j.stage}</span> · {j.progress || 0}%</div>
            </div>
            <div className="w-32 h-1.5 bg-zinc-900 rounded overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.max(j.progress || 0, 3)}%` }} />
            </div>
          </div>
        ))}
        {recentFailures.slice(0, 3).map((j) => (
          <div key={j.job_id} className="flex items-center gap-3" data-testid={`failed-job-${j.job_id}`}>
            <AlertTriangle className="h-3.5 w-3.5 text-rose-400" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-300 truncate">{j.product_name || j.job_id.slice(0, 8)}</div>
              <div className="text-[10px] mono text-rose-300 truncate" title={j.error}>{(j.error || "failed").slice(0, 100)}</div>
            </div>
            <button
              onClick={() => retryJob(j)}
              className="px-2 py-1 rounded border border-rose-700/50 text-[10px] mono text-rose-300 hover:bg-rose-500/10"
              data-testid={`active-retry-${j.job_id}`}
            >
              <RefreshCw className="h-3 w-3 inline mr-1" /> retry
            </button>
          </div>
        ))}
      </div>
    );
  };


  return (
    <>
      <PageHeader
        icon={Film}
        title="Video Library"
        subtitle="Persisted videos across all styles. Click a thumbnail to preview."
        testid="library-header"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGroupByBatch((v) => !v)}
              data-testid="group-by-batch-toggle"
              data-selected={groupByBatch ? "true" : "false"}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors ${
                groupByBatch
                  ? "bg-amber-500/10 border-amber-700/60 text-amber-300"
                  : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              }`}
              title="Group videos that share the same batch_id under one header"
            >
              <Layers className="h-3.5 w-3.5" /> Group by batch
            </button>
          </div>
        }
      />
      <PageBody>
        {renderActiveJobsStrip()}
        {batchFilter && (
          <div
            className="ap-panel p-3 flex items-center justify-between gap-2"
            data-testid="batch-filter-banner"
          >
            <div className="text-xs text-amber-300 mono flex items-center gap-2">
              <Zap className="h-3.5 w-3.5" />
              Filtering to batch <span className="text-amber-200">{batchFilter.slice(0, 8)}…</span>
              <span className="text-zinc-500">({filteredVideos.length} videos)</span>
            </div>
            <button
              onClick={() => setBatchFilter(null)}
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"
              data-testid="batch-filter-clear"
            >
              <X className="h-3 w-3" /> clear
            </button>
          </div>
        )}

        <div data-testid="videos-grid">
        {filteredVideos.length === 0 ? (
          <div className="ap-panel p-8 text-center" data-testid="library-empty">
            <Film className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
            <div className="text-sm text-zinc-400">No videos yet.</div>
            <div className="text-xs text-zinc-600 mt-1">Generate one from the <span className="mono">Generate</span> page.</div>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.key} data-testid={g.batch_id ? `batch-group-${g.batch_id}` : "ungrouped-group"}>
                {groupByBatch && g.batch_id && (
                  <div className="flex items-center gap-2 mb-2 ml-1">
                    <Zap className="h-3.5 w-3.5 text-amber-400" />
                    <h3 className="text-xs mono text-amber-300">
                      Batch of {g.items.length} angle{g.items.length === 1 ? "" : "s"}
                    </h3>
                    <span className="text-[10px] mono text-zinc-600">· {g.batch_id.slice(0, 8)}</span>
                  </div>
                )}
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${
                  groupByBatch && g.batch_id ? "p-2 -mx-2 border border-amber-900/40 rounded-lg bg-amber-500/[0.02]" : ""
                }`}>
                  {g.items.map((v) => <Card key={v.id} v={v} />)}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </PageBody>
      {postingVideo && (
        <PostToTikTokModal video={postingVideo} onClose={() => setPostingVideo(null)} />
      )}
    </>
  );
}
